'use client';

import { ErrorPageShell } from '@/components/ErrorPageShell';
import { NotFoundIllustration } from '@/components/ErrorPageIllustration';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useTranslations } from '@/hooks/useTranslations';

export function NotFoundPage() {
  const { t } = useTranslations();

  const helpfulLinks = [
    { href: '/casinos', label: t('notFound.goToCasinos') },
    { href: '/blog', label: t('notFound.browseGuides') },
    { href: '/game-providers', label: t('nav.gameProviders') },
  ];

  return (
    <ErrorPageShell>
      <section className="relative isolate overflow-hidden px-4 py-14 sm:px-6 sm:py-20">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_20%,rgba(225,29,72,0.24),transparent_30%),radial-gradient(circle_at_85%_10%,rgba(255,183,77,0.18),transparent_34%),linear-gradient(180deg,rgba(15,19,36,0)_0%,rgba(15,19,36,0.82)_100%)]" />
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid overflow-hidden rounded-[2rem] border border-white/10 bg-[#0f1324]/95 shadow-2xl shadow-black/30 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="relative flex min-h-[320px] items-center justify-center border-b border-white/10 bg-white/[0.03] p-6 sm:p-10 lg:border-b-0 lg:border-r">
              <div className="absolute left-6 top-6 rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#ffd89a]">
                {t('notFound.errorCode')}
              </div>
              <NotFoundIllustration className="max-w-xl" />
            </div>

            <div className="p-6 sm:p-10 lg:p-12">
              <p className="inline-flex rounded-full border border-rose-300/20 bg-rose-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-rose-100">
                {t('notFound.kicker')}
              </p>
              <h1 className="mt-5 max-w-xl text-4xl font-bold tracking-tight text-white sm:text-5xl">
                {t('notFound.title')}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-slate-300">
                {t('notFound.description')}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <LocalizedLink
                  href="/casinos"
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#ffb74d] px-6 py-3 text-sm font-bold text-[#3a2100] shadow-lg shadow-[#ffb74d]/20 transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#0f1324]"
                >
                  {t('notFound.goToCasinos')}
                </LocalizedLink>
                <LocalizedLink
                  href="/"
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:border-white/25 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-[#0f1324]"
                >
                  {t('notFound.goHome')}
                </LocalizedLink>
              </div>

              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <p className="text-sm font-semibold text-white">{t('notFound.helpfulTitle')}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {helpfulLinks.map((link) => (
                    <LocalizedLink
                      key={link.href}
                      href={link.href}
                      className="rounded-full border border-white/10 bg-[#15192b] px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-[#ffb74d]/50 hover:text-[#ffd89a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#0f1324]"
                    >
                      {link.label}
                    </LocalizedLink>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ErrorPageShell>
  );
}
